import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BarChart } from '@mui/x-charts/BarChart';
import { getMeCached } from '../authCache';
import { fetchLibraryStudentStatistics } from '../api/library';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import { summarizeRange } from '../api/studyLogs';

const TODAY_TR = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Istanbul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date());
const schoolYearStartIso = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getMonth() >= 7 ? date.getFullYear() : date.getFullYear() - 1;
  return `${year}-08-01`;
};
const ALL_TIME_START = '2000-01-01';

const normalizeItems = (items) => {
  return (items || []).map((item) => ({
    id: String(item?.id ?? ''),
    studentNo: Number(item?.studentNo) || 0,
    firstName: String(item?.firstName ?? item?.first_name ?? ''),
    lastName: String(item?.lastName ?? item?.last_name ?? ''),
    className: String(item?.className ?? item?.class_name ?? ''),
    totalPages: Number(item?.totalPages) || 0,
    totalBooks: Number(item?.totalBooks) || 0,
    completedBooks: (item?.completedBooks || []).map((book) => ({
      bookId: Number(book?.bookId) || 0,
      title: String(book?.title ?? ''),
      pageCount: Number(book?.pageCount) || 0,
      finishedDate: String(book?.finishedDate ?? ''),
    })),
  }));
};

const formatDateTr = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat('tr-TR').format(date);
  } catch (_) {
    return raw;
  }
};

const formatDateShortTr = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit' }).format(date);
  } catch (_) {
    return raw;
  }
};

const weekStartIso = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.getDay(); // 0=Sun ... 6=Sat
  const diff = day === 0 ? -6 : 1 - day; // Monday as week start
  date.setDate(date.getDate() + diff);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const fillMissingWeeks = (groupedMap, valueKey) => {
  const keys = [...groupedMap.keys()].sort((a, b) => String(a).localeCompare(String(b)));
  if (!keys.length) return [];
  const start = new Date(keys[0]);
  const end = new Date(keys[keys.length - 1]);
  const out = [];
  for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 7)) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    const iso = `${y}-${m}-${d}`;
    out.push({ weekStart: iso, [valueKey]: Number(groupedMap.get(iso) || 0) });
  }
  return out;
};

const buildWeeklyCompletedSeries = (books) => {
  const grouped = new Map();
  (books || []).forEach((book) => {
    const iso = weekStartIso(book?.finishedDate);
    if (!iso) return;
    grouped.set(iso, (grouped.get(iso) || 0) + 1);
  });
  return fillMissingWeeks(grouped, 'count');
};

const buildWeeklyPagesSeries = (records) => {
  const grouped = new Map();
  (records || []).forEach((row) => {
    const iso = weekStartIso(row?.date);
    if (!iso) return;
    grouped.set(iso, (grouped.get(iso) || 0) + (Number(row?.pages) || 0));
  });
  return fillMissingWeeks(grouped, 'pages');
};

const LibraryStudentStatisticsPage = () => {
  const navigate = useNavigate();
  const [schoolId, setSchoolId] = useState(null);
  const [isTeacher, setIsTeacher] = useState(false);
  const [statisticsPeriod, setStatisticsPeriod] = useState('schoolYear');
  const [classes, setClasses] = useState([]);
  const [selectedClassName, setSelectedClassName] = useState('');
  const [students, setStudents] = useState([]);
  const [expandedStudentId, setExpandedStudentId] = useState('');
  const [readingSeriesByStudent, setReadingSeriesByStudent] = useState({});
  const [readingLoadingByStudent, setReadingLoadingByStudent] = useState({});
  const [readingErrorByStudent, setReadingErrorByStudent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        const me = await getMeCached();
        const resolvedSchoolId = me?.data?.admin_user?.school_id;
        if (!me?.ok || !resolvedSchoolId) {
          throw new Error('Okul bilgisi bulunamadı.');
        }
        if (!cancelled) {
          setSchoolId(resolvedSchoolId);
          setIsTeacher(String(me?.data?.admin_user?.role || '').toLowerCase() === 'teacher');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Öğrenci istatistikleri yüklenemedi.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchClasses(schoolId);
        if (cancelled) return;
        const sorted = (list || []).sort((a, b) =>
          String(a?.name || '').localeCompare(String(b?.name || ''), 'tr-TR'),
        );
        setClasses(sorted);
      } catch (_) {
        if (!cancelled) setClasses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const payload = await fetchLibraryStudentStatistics(schoolId, {
          className: selectedClassName || undefined,
          period: statisticsPeriod,
        });
        if (cancelled) return;
        setStudents(normalizeItems(payload?.students || []));
      } catch (e) {
        if (!cancelled) {
          setStudents([]);
          setError(e?.message || 'Öğrenci istatistikleri yüklenemedi.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedClassName, statisticsPeriod]);

  useEffect(() => {
    setReadingSeriesByStudent({});
    setReadingErrorByStudent({});
    setExpandedStudentId('');
  }, [statisticsPeriod]);

  const sortedStudents = useMemo(() => {
    return [...students].sort((a, b) => {
      const aNo = Number(a?.studentNo || 0);
      const bNo = Number(b?.studentNo || 0);
      if (aNo !== bNo) return aNo - bNo;
      return String(a?.firstName || '').localeCompare(String(b?.firstName || ''), 'tr-TR');
    });
  }, [students]);

  const totals = useMemo(() => {
    return sortedStudents.reduce(
      (acc, student) => {
        acc.pages += Number(student?.totalPages || 0);
        acc.books += Number(student?.totalBooks || 0);
        return acc;
      },
      { pages: 0, books: 0 },
    );
  }, [sortedStudents]);

  const loadStudentReadingSeries = async (studentId) => {
    const sid = String(studentId || '');
    if (!sid || !schoolId) return;
    if (Array.isArray(readingSeriesByStudent[sid]) || readingLoadingByStudent[sid]) return;
    setReadingLoadingByStudent((prev) => ({ ...prev, [sid]: true }));
    setReadingErrorByStudent((prev) => ({ ...prev, [sid]: '' }));
    try {
      const startDate = statisticsPeriod === 'all' ? ALL_TIME_START : schoolYearStartIso(TODAY_TR);
      const data = await summarizeRange(schoolId, sid, startDate, TODAY_TR);
      const records = (data?.records || [])
        .map((row) => ({
          date: String(row?.date || ''),
          pages: Number(row?.pages || 0),
        }))
        .filter((row) => row.date)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
      setReadingSeriesByStudent((prev) => ({ ...prev, [sid]: records }));
    } catch (e) {
      setReadingErrorByStudent((prev) => ({ ...prev, [sid]: e?.message || 'Grafik verisi alınamadı.' }));
      setReadingSeriesByStudent((prev) => ({ ...prev, [sid]: [] }));
    } finally {
      setReadingLoadingByStudent((prev) => ({ ...prev, [sid]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Öğrenci istatistikleri yükleniyor...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Öğrenci İstatistikleri</Typography>
          <Typography variant="body2" color="text.secondary">
            Toplam {sortedStudents.length} öğrenci • {totals.books} kitap • {totals.pages} sayfa
          </Typography>
        </Box>
        <Box
          component="button"
          onClick={() => navigate('/admin')}
          style={{
            border: 'none',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            color: '#1565c0',
            fontWeight: 600,
          }}
        >
          <ArrowBackIcon fontSize="small" /> Panele dön
        </Box>
      </Paper>

      {classes.length > 1 ? (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControl fullWidth size="small" sx={{ flex: '1 1 220px' }}>
            <InputLabel id="library-stats-class-filter-label">Sınıf</InputLabel>
            <Select
              labelId="library-stats-class-filter-label"
              value={selectedClassName}
              label="Sınıf"
              onChange={(e) => setSelectedClassName(String(e.target.value || ''))}
            >
              <MenuItem value="">Tüm Sınıflar</MenuItem>
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={String(cls.name || '')}>{String(cls.name || '')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {isTeacher ? (
            <FormControl size="small" sx={{ flex: '1 1 220px' }}>
              <InputLabel id="library-stats-period-filter-label">Dönem</InputLabel>
              <Select
                labelId="library-stats-period-filter-label"
                value={statisticsPeriod}
                label="Dönem"
                onChange={(e) => setStatisticsPeriod(String(e.target.value || 'schoolYear'))}
              >
                <MenuItem value="schoolYear">Bu Öğrenim Yılı</MenuItem>
                <MenuItem value="all">Tümü</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          </Box>
        </Paper>
      ) : isTeacher ? (
        <Paper sx={{ p: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="library-stats-period-filter-label">Dönem</InputLabel>
            <Select
              labelId="library-stats-period-filter-label"
              value={statisticsPeriod}
              label="Dönem"
              onChange={(e) => setStatisticsPeriod(String(e.target.value || 'schoolYear'))}
            >
              <MenuItem value="schoolYear">Bu Öğrenim Yılı</MenuItem>
              <MenuItem value="all">Tümü</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!error && sortedStudents.length === 0 ? (
        <Alert severity="info">Bu kapsamda öğrenci verisi bulunamadı.</Alert>
      ) : null}

      {!error && sortedStudents.map((student) => (
        <Accordion
          key={student.id}
          disableGutters
          expanded={expandedStudentId === student.id}
          onChange={(_, expanded) => {
            setExpandedStudentId(expanded ? student.id : '');
            if (expanded) loadStudentReadingSeries(student.id);
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1, gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {student.studentNo ? `${student.studentNo} - ` : ''}{student.firstName} {student.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">Sınıf: {student.className || '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ px: 1.2, py: 0.4, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{student.totalBooks} kitap</Typography>
                </Box>
                <Box sx={{ px: 1.2, py: 0.4, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{student.totalPages} sayfa</Typography>
                </Box>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Haftalık Okunan Sayfa ({statisticsPeriod === 'all' ? 'Tüm Zamanlar' : 'Bu Öğrenim Yılı'})
              </Typography>
              {readingLoadingByStudent[student.id] ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Grafik yükleniyor...</Typography>
                </Box>
              ) : readingErrorByStudent[student.id] ? (
                <Alert severity="warning">{readingErrorByStudent[student.id]}</Alert>
              ) : (() => {
                const points = Array.isArray(readingSeriesByStudent[student.id]) ? readingSeriesByStudent[student.id] : [];
                const weeklyPages = buildWeeklyPagesSeries(points);
                if (!weeklyPages.length) {
                  return <Typography variant="body2" color="text.secondary">Study log üzerinde sayfa kaydı bulunamadı.</Typography>;
                }
                const xData = weeklyPages.map((_, idx) => idx);
                const mid = Math.floor((xData.length - 1) / 2);
                const last = Math.max(0, xData.length - 1);
                return (
                  <BarChart
                    height={220}
                    slotProps={{ legend: { hidden: true } }}
                    xAxis={[{
                      scaleType: 'band',
                      data: xData,
                      valueFormatter: (value) => {
                        if (value === 0) return formatDateShortTr(weeklyPages[0]?.weekStart);
                        if (value === mid) return formatDateShortTr(weeklyPages[mid]?.weekStart);
                        if (value === last) return formatDateShortTr(weeklyPages[last]?.weekStart);
                        return '';
                      },
                      tickLabelStyle: { fontSize: 11 },
                    }]}
                    series={[{
                      data: weeklyPages.map((p) => Number(p.pages || 0)),
                      color: '#2563eb',
                    }]}
                    margin={{ top: 12, right: 16, bottom: 28, left: 36 }}
                    tooltip={{ trigger: 'axis' }}
                  />
                );
              })()}
            </Box>
            <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Haftalık Bitirilen Kitap Sayısı ({statisticsPeriod === 'all' ? 'Tüm Zamanlar' : 'Bu Öğrenim Yılı'})
              </Typography>
              {(() => {
                const weekly = buildWeeklyCompletedSeries(student.completedBooks || []);
                if (!weekly.length) {
                  return <Typography variant="body2" color="text.secondary">Haftalık tamamlanan kitap kaydı bulunamadı.</Typography>;
                }
                const xData = weekly.map((_, idx) => idx);
                const mid = Math.floor((xData.length - 1) / 2);
                const last = Math.max(0, xData.length - 1);
                return (
                  <BarChart
                    height={220}
                    slotProps={{ legend: { hidden: true } }}
                    xAxis={[{
                      scaleType: 'band',
                      data: xData,
                      valueFormatter: (value) => {
                        if (value === 0) return formatDateShortTr(weekly[0]?.weekStart);
                        if (value === mid) return formatDateShortTr(weekly[mid]?.weekStart);
                        if (value === last) return formatDateShortTr(weekly[last]?.weekStart);
                        return '';
                      },
                      tickLabelStyle: { fontSize: 11 },
                    }]}
                    series={[{
                      data: weekly.map((p) => Number(p.count || 0)),
                      color: '#16a34a',
                    }]}
                    margin={{ top: 12, right: 16, bottom: 28, left: 36 }}
                    tooltip={{ trigger: 'axis' }}
                  />
                );
              })()}
            </Box>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Kitap</TableCell>
                    <TableCell align="right">Sayfa</TableCell>
                    <TableCell>Tamamlanma</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(student.completedBooks || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">Tamamlanan kitap yok.</TableCell>
                    </TableRow>
                  ) : (
                    (student.completedBooks || []).map((book, idx) => (
                      <TableRow
                        key={`${student.id}-${book.bookId}-${idx}`}
                        sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell>{book.title || '-'}</TableCell>
                        <TableCell align="right">{book.pageCount || 0}</TableCell>
                        <TableCell>{formatDateTr(book.finishedDate)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default LibraryStudentStatisticsPage;
